import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import { cog } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { ModuleActions } from '@galo-monitor/stores/module'
import colors from '@galo-monitor/styles/colors'
import DataTable from '@galo-monitor/web-components/DataTable'
import Icon from '@galo-monitor/web-components/Icon'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import ModuleApplicationsModal from '@galo-monitor/web-containers/modals/root/ModuleApplicationsModal'
import ModuleModal from '@galo-monitor/web-containers/modals/root/ModuleModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const ModulesScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: modules, mutate, isValidating } = useGaloMonitorApi('/admin/modules')

  const userActions = useMemo(
    () => getActionsBySlug(routes.modules.slug),
    [getActionsBySlug, routes.modules.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('context'),
        field: 'contextName',
        defaultGroupSort: 'asc',
        defaultGroupOrder: 0
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-module',
        Component: ModuleModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, mod) => {
      createDialog({
        id: 'edit-module',
        Component: ModuleModal,
        props: {
          mod,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, mod) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('module', { howMany: 1 }),
            name: mod.name
          }),
          onConfirm: () => {
            dispatch(ModuleActions.removeModule(
              mod,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('module', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'module-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('module', { howMany: 1 }))
                    })  }.`,
                    message: `${ t('you need detach all permissions to delete a module', {
                      this: t('module', { howMany: 1 }),
                      action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push({
          icon: () => <Icon icon={ cog } color={ colors.darkGrey } />,
          tooltip: t('configure {this}', { this: t('permission', { howMany: 2 }) }),
          onClick: (_, mod) => {
            createDialog({
              id: 'module-applications',
              Component: ModuleApplicationsModal,
              props: { mod }
            })
          },
          iconButtonProps: {
            size: 'small'
          },
          position: 'row'
        })
      }
      return datatableActions
    },
    [createDialog, t, userActions.edit]
  )

  const crumbs = useMemo(() => [
    {
      title: t('module', { howMany: 2 }),
      key: 'modules-screen',
      url: '/modules',
      active: true
    }
  ], [t])

  return (
    <Screen title={ routes.modules.title } breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ modules || [] }
        columns={ columns }
        actions={ actions }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        onDeleteClick={ userActions.delete && onDeleteClick }
        options={ {
          defaultExpanded: true
        } }
      />
    </Screen>
  )
}

export default ModulesScreen
